import CloseIcon from '@mui/icons-material/Close';
import { Tooltip } from '@mui/material';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import {
	type FC,
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ChangeEvent
} from 'react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';
import { useParams } from 'react-router-dom';
import Switch from '../../../../components/core/Switch/Switch';
import Table from '../../../../components/Table/Table';
import {
	useGetProgramNotificationsSettingsQuery,
	useUpdateProgramNotificationsSettingsMutation
} from '../../../../services/endpoints/notificationsEndpoints';
import type { ProgramNotificationRow } from '../../../../services/endpoints/notificationsEndpoints';
import { getLocalAuth } from '../../../../utils/environmentUtils';
import BasicCellText from './BasicCellText';
import { useAppSelector } from '../../../../redux/hooks';
import usePermissions from '../../../../hooks/usePermissions';
import { ALLOWED_ACCOUNT_TYPES_FOR_PROGRAM_NOTIFICATION_SETTINGS } from '../../../../constants/common';

interface AlertBalloonState {
	isOpen: boolean;
	message?: string;
	status?: 'success' | 'error' | 'warning' | 'info';
}

interface CellValues {
	id: string | number;
	row: ProgramNotificationRow;
	formattedValue?: unknown;
}

const ProgramNotifications: FC = () => {
	let { programID } = useParams<{ programID: string }>();
	programID = programID?.replace('p-', '');

	const accountSource = useAppSelector((state) => state?.login?.accountSource);
	const apiToken = getLocalAuth(accountSource)?.access_token;
	const { userAccountTypeID } = usePermissions();

	const canModifySettings =
		ALLOWED_ACCOUNT_TYPES_FOR_PROGRAM_NOTIFICATION_SETTINGS.includes(userAccountTypeID);

	const [updateProgramNotificationsSettings] = useUpdateProgramNotificationsSettingsMutation();

	const { data, isLoading, error } = useGetProgramNotificationsSettingsQuery(
		{
			programID,
			apiToken
		},
		{ refetchOnMountOrArgChange: true }
	);

	const [rows, setRows] = useState<ProgramNotificationRow[]>([]);
	const [rowCount, setRowCount] = useState<number>(0);

	useEffect(() => {
		if (data) {
			setRows(data);
			setRowCount(data.length);
		}
	}, [isLoading, data]);

	const isSettingUpdatingRef = useRef<Record<string | number, boolean>>({});

	const [alertBalloon, setAlertBalloon] = useState<AlertBalloonState>({ isOpen: false });

	const handleAlert = useCallback(
		(message: string, status: 'success' | 'error' | 'warning' | 'info'): void => {
			setAlertBalloon({ isOpen: true, message, status });
			setTimeout(() => {
				setAlertBalloon({ isOpen: false, status });
			}, 10000);
		},
		[]
	);

	const getMessage = useCallback(
		(response: ProgramNotificationRow | undefined, isEnabled: boolean): string => {
			if (response?.email_template?.email_template_name) {
				return `${response.email_template.email_template_name} notifications ${
					isEnabled ? 'enabled' : 'disabled'
				}.`;
			}
			return `Notification ${isEnabled ? 'enabled' : 'disabled'}.`;
		},
		[]
	);

	const handleChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>, settingId: string | number): void => {
			const newSettingValue = event.target.checked;

			// Store the previous value to revert if the update fails
			const previousValue = rows.find(
				(row) => row.program_notification_settings_id === settingId
			)?.active;

			// Optimistically update the UI
			setRows((prevRows) =>
				prevRows.map((row) =>
					row.program_notification_settings_id === settingId
						? { ...row, active: newSettingValue }
						: row
				)
			);

			if (isSettingUpdatingRef.current[settingId]) {
				return;
			} else {
				isSettingUpdatingRef.current[settingId] = true;
			}

			updateProgramNotificationsSettings({
				id: String(settingId),
				enabled: newSettingValue,
				apiToken: apiToken,
				programID: programID
			})
				.then(
					(
						result:
							| { data?: ProgramNotificationRow }
							| { error: FetchBaseQueryError | SerializedError }
					) => {
						if ('error' in result) {
							// Revert the UI to the previous value
							setRows((prevRows) =>
								prevRows.map((row) =>
									row.program_notification_settings_id === settingId
										? { ...row, active: previousValue ?? false }
										: row
								)
							);
							handleAlert('Error updating setting', 'error');
						} else if ('data' in result && result?.data) {
							const message = getMessage(result?.data, newSettingValue);
							message?.length && handleAlert(message, 'success');
						}
						isSettingUpdatingRef.current[settingId] = false;
					}
				)
				.catch((error: unknown) => {
					// Revert the UI to the previous value
					setRows((prevRows) =>
						prevRows.map((row) =>
							row.program_notification_settings_id === settingId
								? { ...row, active: previousValue ?? false }
								: row
						)
					);
					console.error('Error updating setting:', error);
					handleAlert('Error updating setting', 'error');
					isSettingUpdatingRef.current[settingId] = false;
				});
		},
		[updateProgramNotificationsSettings, apiToken, programID, getMessage, handleAlert, rows]
	);

	const renderEmailNotificationCell = useCallback(
		(cellValues: CellValues) => {
			const isOrgActive = cellValues?.row?.org_active;
			const emailTemplateName = cellValues?.row?.email_template?.email_template_name;
			const isDisabled = isOrgActive === false || !canModifySettings;
			return (
				<BasicCellText
					cellValues={{ ...cellValues, formattedValue: emailTemplateName }}
					disabled={isDisabled}
				/>
			);
		},
		[canModifySettings]
	);

	const renderDescriptionCell = useCallback(
		(cellValues: CellValues) => {
			const isOrgActive = cellValues?.row?.org_active;
			const description = cellValues?.row?.email_template?.description;
			const isDisabled = isOrgActive === false || !canModifySettings;
			return (
				<BasicCellText
					cellValues={{ ...cellValues, formattedValue: description }}
					disabled={isDisabled}
				/>
			);
		},
		[canModifySettings]
	);

	const renderActiveCell = useCallback(
		(cellValues: CellValues) => {
			const settingId = cellValues?.id;
			const isOrgActive = cellValues?.row?.org_active;
			const isDisabledByAdmin = isOrgActive === false;
			const isDisabled = isDisabledByAdmin || !canModifySettings;
			const tooltipTitle = isDisabledByAdmin
				? 'Notification disabled by Administrator'
				: !canModifySettings
				? 'Please contact your organizations master account holder to make any changes to this setting'
				: '';
			// When disabled by admin, show as toggled off regardless of actual value
			// Otherwise, show the actual value
			const isChecked = isDisabledByAdmin ? false : !!cellValues?.formattedValue;
			return (
				<Tooltip title={tooltipTitle} arrow placement="top">
					<div>
						<Switch
							id={`enabled-toggle-${cellValues?.id}`}
							disabled={isDisabled || isSettingUpdatingRef.current[settingId]}
							onChange={(event: ChangeEvent<HTMLInputElement>) => {
								handleChange(event, settingId);
							}}
							checked={isChecked}
						/>
					</div>
				</Tooltip>
			);
		},
		[handleChange, canModifySettings]
	);

	const columns = useMemo(
		() => [
			{
				field: 'email_template_name',
				headerName: 'Email Notification',
				description: 'Email notification to be sent',
				flex: 1,
				minWidth: 350,
				renderCell: renderEmailNotificationCell
			},
			{
				field: 'description',
				headerName: 'Description',
				description: 'Description of the email notification',
				flex: 2,
				minWidth: 300,
				renderCell: renderDescriptionCell
			},
			{
				field: 'active',
				headerName: 'Notification Enabled',
				description: 'Indicates if the email notification is sent for this program',
				flex: 1,
				minWidth: 120,
				renderCell: renderActiveCell
			}
		],
		[renderEmailNotificationCell, renderDescriptionCell, renderActiveCell]
	);

	const getRowId = useCallback(
		(cell: ProgramNotificationRow) => cell?.program_notification_settings_id,
		[]
	);

	const isRowSelectable = useCallback(
		(params: { row: ProgramNotificationRow }) => {
			return params.row.org_active !== false && canModifySettings;
		},
		[canModifySettings]
	);

	const getErrorMessage = useCallback(() => {
		if (error && 'status' in error) {
			const fetchError = error as FetchBaseQueryError;
			if (fetchError.status === 403) {
				return 'Unable to view these notification settings due to current permissions';
			}
		}
		return 'Unable to retrieve notification preferences';
	}, [error]);

	return (
		<div>
			<Collapse in={alertBalloon.isOpen}>
				<Alert
					severity={alertBalloon?.status || 'info'}
					action={
						<IconButton
							aria-label="close"
							color={alertBalloon?.status || 'info'}
							size="small"
							onClick={() => {
								setAlertBalloon({ isOpen: false });
							}}
						>
							<CloseIcon fontSize="inherit" />
						</IconButton>
					}
					sx={{ mb: 2, mt: 2 }}
				>
					{alertBalloon?.message}
				</Alert>
			</Collapse>

			{error && (
				<Stack sx={{ width: '100%', marginTop: '20px' }} spacing={2}>
					<Alert severity="error">{getErrorMessage()}</Alert>
				</Stack>
			)}

			{!error && !isLoading && rowCount === 0 && (
				<Stack sx={{ width: '100%', marginTop: '20px' }} spacing={2}>
					<Alert severity="info">Unable to retrieve notification preferences</Alert>
				</Stack>
			)}

			{!error && rows && rowCount > 0 && (
				<Table
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore we need to convert Table to TS to fix this ignore
					getRowId={getRowId}
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore we need to convert Table to TS to fix this ignore
					columns={columns}
					rowHeight={true}
					enableAutoPageSize
					rows={rows}
					rowCount={rowCount}
					loading={isLoading}
					pagination={false}
					hideFilters
					hideTableActions
					isRowSelectable={isRowSelectable}
				/>
			)}
		</div>
	);
};

const MemoizedProgramNotifications = memo(ProgramNotifications);
MemoizedProgramNotifications.displayName = 'ProgramNotifications';

export default MemoizedProgramNotifications;
